import moment from 'moment';
import { BookingCategory, RoomBooking } from './Types';

export const copyObj = (obj: Object) => JSON.parse(JSON.stringify(obj || {}))

export const convertTimeIntoMinutes = (time: string) => {
  if (time === '') {
    return 0
  }
  let timeArray = time.split(' ')
  let timeStr = timeArray[0]
  let timePMAM = timeArray[1]
  let timeMinutesArray = timeStr.split(':')
  let hours = timeMinutesArray[0]
  let minutes = timeMinutesArray[1]
  let totalMinutes = 0
  if (timePMAM === 'AM') {
    totalMinutes = Number(hours) * 60 + Number(minutes)
  } else if (timePMAM === 'PM' && Number(hours) === 12) {
    totalMinutes = Number(hours) * 60 + Number(minutes)
  } else {
    totalMinutes = Number(hours) * 60 + Number(minutes) + 12 * 60
  }
  return totalMinutes
}
export const isNull = (expression: any, ret: any) => {
  if (expression === null || expression === undefined) {
    return ret
  }
  return expression
}
export const updateWorkingDays = (date: Date, updateInDays: number) => {
  if (updateInDays >= 0) {
    for (var i = 0; i < updateInDays; i++) {
      date.setDate(date.getDate() + 1)
      if (date.getDay() === 0) {
        date.setDate(date.getDate() + 1)
      } else if (date.getDay() === 6) {
        date.setDate(date.getDate() + 2)
      }
    }
  } else {
    updateInDays = Math.abs(updateInDays)
    for (var num = 0; num < updateInDays; num++) {
      date.setDate(date.getDate() - 1)
      if (date.getDay() === 0) {
        date.setDate(date.getDate() - 2)
      } else if (date.getDay() === 6) {
        date.setDate(date.getDate() - 1)
      }
    }
  }
  return date.getDate()
}

export const convertMinutesIntoTime = (time: number) => {
  if (time === 0) {
    return "00:00 AM"
  }
  let period = 'AM'
  let hours = Math.floor(time / 60)
  let minutes: any = Math.floor(time % 60)
  if (minutes === 0) {
    minutes = '00'
  }
  if (hours > 11) {
    period = 'PM'
  }
  if (hours > 12) {
    hours = hours - 12
  }
  let timeSlot = `${hours}:${minutes} ${period}`
  return timeSlot
}

export const convertDbTimeIntoString = (time: number | undefined) => {
  if (time === undefined || time === null) {
    return '';
  }
  return moment()
    .startOf('day')
    .add(time, 'minutes')
    .format('hh:mm A');
};

export const convertDbTimeIntoInput = (minutes: number | undefined): string => {
  if (minutes === undefined || minutes === null) {
    return '';
  }
  return moment()
    .startOf('day')
    .add(minutes, 'minutes')
    .format('HH:mm');
};

// Helper to convert "HH:mm" string to Date
export const stringToDate = (time: string) => {
  if (!time) return null;
  const [hours, minutes] = time.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

// Helper to convert Date to "HH:mm" string
export const dateToString = (date: Date | null) =>
  date ? date.toTimeString().slice(0, 5) : '';

// Helper to convert Date to minutes since midnight
export const dateToMinutes = (date: Date | null): number => {
  if (!date) return 0;
  return date.getHours() * 60 + date.getMinutes();
};

// Helper to convert minutes since midnight to Date
export const minutesToDate = (minutes: number): Date => {
  const date = new Date();
  date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return date;
};


export const getRoomBooking = (roomBooking: RoomBooking[] | undefined, bookingCategory: BookingCategory) => {
  // find the roomBooking that matches BookingCategory
  // plus special case for blocked events: category is null for blocked events
  const booking = roomBooking?.find(booking => booking.category === bookingCategory || booking.category === null);
  return booking ? {
    id: booking.id,
    date: booking.date,
    startTime: booking.startTime,
    endTime: booking.endTime,
    category: booking.category,
    formattedDate: formatDate(booking.date, 'date'),
    formattedStartTime: convertDbTimeIntoString(booking.startTime),
    formattedEndTime: convertDbTimeIntoString(booking.endTime),
  } : null;
};

// Helper function to parse dates that can come in different formats and return a Date object
export const parseDate = (dateValue: any): Date | null => {
  if (!dateValue) return null;
  
  try {
    let timestamp: number;
    
    // If it's already a Date object
    if (dateValue instanceof Date) {
      return dateValue;
    }
    // If it's a number (timestamp)
    else if (typeof dateValue === 'number') {
      timestamp = dateValue;
    }
    // If it's a string, try to parse it
    else if (typeof dateValue === 'string') {
      // Try parsing as timestamp first
      const parsedTimestamp = Number(dateValue);
      if (!isNaN(parsedTimestamp)) {
        timestamp = parsedTimestamp;
      } else {
        // Try parsing as ISO string
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) {
          return null;
        }
        return date;
      }
    } else {
      return null;
    }
    
    return new Date(timestamp);
  } catch (error) {
    return null;
  }
};

// Helper function to format dates that can come in different formats
export const formatDate = (dateValue: any, format: 'datetime' | 'date' | 'moment' = 'datetime'): string | moment.Moment | null => {
  const parsedDate = parseDate(dateValue);
  if (!parsedDate) return format === 'moment' ? null : 'N/A';
  
  try {
    const momentDate = moment.utc(parsedDate.getTime());
    
    switch (format) {
      case 'date':
        return momentDate.format('MM/DD/YYYY');
      case 'moment':
        return momentDate;
      case 'datetime':
      default:
        return momentDate.format('MM/DD/YYYY hh:mm A');
    }
  } catch (error) {
    return format === 'moment' ? null : 'Invalid Date';
  }
};

// formatDateSystem: Use for displaying dates that are set by the server system
// at API time (e.g., createdAt/updatedAt). Server records these in UTC, but the
// application displays them in the Eastern Time Zone. This function converts
// UTC timestamps to America/New_York while handling DST automatically.
export const formatDateSystem = (
  dateValue: any,
  format: 'datetime' | 'date' = 'datetime'
): string => {
  const parsedDate = parseDate(dateValue);
  if (!parsedDate) return 'N/A';

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(format === 'datetime'
        ? { hour: '2-digit', minute: '2-digit', hour12: true }
        : {})
    }).formatToParts(parsedDate);

    const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';

    const mm = get('month');
    const dd = get('day');
    const yyyy = get('year');

    if (format === 'date') {
      return `${mm}/${dd}/${yyyy}`;
    }

    // Hour from Intl may come with leading space depending on environment; trim it
    const hh = (get('hour') || '').padStart(2, '0');
    const min = get('minute');
    const dayPeriod = get('dayPeriod');

    return `${mm}/${dd}/${yyyy} ${hh}:${min} ${dayPeriod}`;
  } catch (error) {
    return 'Invalid Date';
  }
};

// Validates if a dry run date is within the allowed bounds relative to a live event date
// Returns null if validation cannot be performed (missing data), true if valid, false if invalid
export const validateDryRunDate = (
  eventLiveDate: Date | null,
  eventDryDate: Date | null,
  updateWorkingDays: (date: Date, days: number) => number
): boolean | null => {
  // Return null if we can't validate (missing data) - don't clear dry date in this case
  if (!eventLiveDate || !eventDryDate) {
    return null;
  }

  // First, explicitly check that dry run date is BEFORE live event date
  const dryMoment = moment(eventDryDate).startOf('day');
  const liveMoment = moment(eventLiveDate).startOf('day');
  
  // Dry run must be before live event date
  if (!dryMoment.isBefore(liveMoment)) {
    return false;
  }

  // Calculate minimum dry date (3 working days from today)
  const minimumDryDate = new Date();
  updateWorkingDays(minimumDryDate, 3);

  // Calculate maximum dry date (3 working days before live event)
  const maximumDryDate = new Date(eventLiveDate);
  updateWorkingDays(maximumDryDate, -3);

  // Validate dry date is within bounds
  const minMoment = moment(minimumDryDate).startOf('day');
  const maxMoment = moment(maximumDryDate).startOf('day');

  return !dryMoment.isBefore(minMoment) && !dryMoment.isAfter(maxMoment);
};